import { useState } from 'react';
import styled from 'styled-components6';
import { When } from 'react-if';
import type { PluginMedia } from 'rwl-shared/dist/models/plugin/Plugin';
import { ButtonContainer, NextButton, PrevButton } from './CarouselButtons';
import { mqDown, mqUp } from '../../../../theme';
import { CarouselDots } from './CarouselDots';
import { CarouselCounts } from './CarouselCounts';

export type MediaVariants = 'light' | 'dark';

const CarouselContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
    //if browser has the ability to hover (has a mouse), then show the buttons on hover
    @media (hover: hover) {
        &:hover {
            ${ButtonContainer} {
                visibility: visible;
                opacity: 1;
            }
        }
    }
`;

const CarouselImage = styled.img`
    border-radius: var(--rwl-spacing-sm) 0 0 var(--rwl-spacing-sm);
    height: 100%;
    width: var(--rwl-jobCardMediaWidth-lg);
    object-fit: cover;
    ${mqUp('xl')} {
        aspect-ratio: 50/43;
    }
    ${mqDown('xl')} {
        aspect-ratio: 42/50;
        width: var(--rwl-jobCardMediaWidth-md);
    }
    ${mqDown('sm')} {
        aspect-ratio: 1/1;
        width: var(--rwl-jobCardMediaWidth-sm);
    }
    ${mqDown('xs')} {
        aspect-ratio: 35/50;
        width: var(--rwl-jobCardMediaWidth-xs);
    }
`;

type CarouselProps = {
    disabledCarousel: boolean;
    images: PluginMedia[];
    variant?: MediaVariants;
    onChange?: (media: PluginMedia, index: number) => void;
};

export const MediaCarousel = ({
    disabledCarousel,
    images,
    variant = 'light',
    onChange,
}: CarouselProps): JSX.Element | null => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [touchStartX, setTouchStartX] = useState<number | null>(null);
    const safeImages = images ?? [];

    // Guard against empty images array
    if (safeImages.length === 0) {
        return null;
    }

    // Reset to first image if index is out of bounds (e.g., when switching to a job with fewer images)
    // This synchronous calculation replaces the previous useEffect reset, which could race with user interactions
    const safeIndex =
        currentImageIndex < safeImages.length ? currentImageIndex : 0;

    // No adjacent-image preload here on purpose. A detached `Image` download
    // ignores `loading="lazy"`, so with hundreds of cards mounted off-screen it
    // fetched every neighbour thumbnail on mount and again on every re-render
    // (EN-1554). Navigation fetches the next image on demand instead.

    // Unified handler for index changes - ensures onChange is always called
    const handleIndexChange = (newIndex: number) => {
        setCurrentImageIndex(newIndex);
        onChange?.(safeImages[newIndex], newIndex);
    };

    const nextImage = (e: any) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        const newIndex = (safeIndex + 1) % safeImages.length;
        handleIndexChange(newIndex);
    };

    const prevImage = (e: any) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        const newIndex =
            (safeIndex - 1 + safeImages.length) % safeImages.length;
        handleIndexChange(newIndex);
    };

    const handleTouchStart = (e: any) => {
        setTouchStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e: any) => {
        if (touchStartX !== null) {
            const currentX = e.touches[0].clientX;
            const diff = touchStartX - currentX;

            if (diff > 5) {
                nextImage(e);
            } else if (diff < -5) {
                prevImage(e);
            }
            setTouchStartX(null);
        }
    };

    const currentImage = safeImages[safeIndex];
    if (!currentImage) {
        return null;
    }

    return (
        <CarouselContainer
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
        >
            <CarouselImage
                loading="lazy"
                className="carousel-image"
                src={currentImage.thumbnailUrl}
                alt="carousel"
            />
            <When condition={safeImages.length > 1 && !disabledCarousel}>
                <ButtonContainer variant={variant}>
                    <PrevButton onButtonClick={prevImage} variant={variant} />
                    <NextButton onButtonClick={nextImage} variant={variant} />
                </ButtonContainer>
                <When condition={variant === 'light'}>
                    <CarouselDots
                        totalImages={safeImages.length}
                        currentImageIndex={safeIndex}
                        setCurrentImageIndex={handleIndexChange}
                    />
                </When>
                <When condition={variant === 'dark'}>
                    <CarouselCounts
                        totalImages={safeImages.length}
                        currentImageIndex={safeIndex}
                    />
                </When>
            </When>
        </CarouselContainer>
    );
};
